import type { DocNode } from '@atlaskit/adf-schema/doc';
import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import { getDocument } from '@atlaskit/browser-apis';
import { JSONTransformer } from '@atlaskit/editor-json-transformer/JSONTransformer-2';
import { MarkdownTransformer } from '@atlaskit/editor-markdown-transformer';
import { DOMParser, type Schema } from '@atlaskit/editor-prosemirror/model';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import {
	AdfParseError,
	AdfParser,
	AdfRepairStrategy,
	AdfSchemaValidator,
} from '@atlassian/adf-parser';
import { convertMarkdownToProsemirror } from '@atlassian/ai-model-io/convert-markdown-to-prosemirror';
import { htmlToAdf } from '@atlassian/atlassian-html-format';
import type { MultiFormatContentType } from '@atlassian/editor-plugin-ai-streaming-orchestrator/types';

const jsonTransformer = new JSONTransformer();

const createFallbackDoc = (text: string): DocNode => ({
	version: 1,
	type: 'doc',
	content: [
		{
			type: 'paragraph',
			content: [{ type: 'text', text }],
		},
	],
});

export interface ToolProcessorChunkStrategy {
	processChunk: (delta: string, isFinished: boolean) => string;
}

class AdfChunkStrategy implements ToolProcessorChunkStrategy {
	private parser: AdfParser | undefined;

	constructor(schema?: Schema) {
		if (schema) {
			this.parser = new AdfParser({
				schemaValidator: new AdfSchemaValidator(schema),
				repairStrategy: new AdfRepairStrategy(schema),
			});
		}
	}

	processChunk(delta: string, isFinished: boolean): string {
		if (!this.parser) {
			return delta;
		}

		try {
			this.parser.processChunk(delta, isFinished);
		} catch (error) {
			if (!(error instanceof AdfParseError)) {
				throw error;
			}
		}

		return JSON.stringify(this.parser.getPartialDocument()) ?? '';
	}
}

class MarkdownChunkStrategy implements ToolProcessorChunkStrategy {
	private accumulated = '';
	private markdownTransformer: MarkdownTransformer | undefined;
	private schema: Schema;
	private useMarkdownPlus: boolean;

	constructor(schema?: Schema) {
		this.schema = schema ?? defaultSchema;
		this.useMarkdownPlus = expValEquals(
			'platform_editor_use_markdown_plus_parser',
			'isEnabled',
			true,
		);
	}

	private getMarkdownTransformer(): MarkdownTransformer {
		if (!this.markdownTransformer) {
			this.markdownTransformer = new MarkdownTransformer(this.schema);
		}
		return this.markdownTransformer;
	}

	processChunk(delta: string): string {
		this.accumulated += delta;

		try {
			if (this.useMarkdownPlus) {
				const { pmFragment } = convertMarkdownToProsemirror({
					schema: this.schema,
					markdown: this.accumulated,
					idMap: {},
					featureToggles: {
						markdownPlus: true,
						markdownPlusExtensions: true,
						markdownPlusPanels: true,
						markdownPlusDecisions: true,
						markdownPlusRemoveUnclosedTokens: true,
						allowAttributesMatchDefault: true,
					},
				});
				const doc = this.schema.nodes.doc.create(null, pmFragment);
				return JSON.stringify(jsonTransformer.encode(doc) as DocNode);
			}

			return JSON.stringify(
				jsonTransformer.encode(this.getMarkdownTransformer().parse(this.accumulated)) as DocNode,
			);
		} catch {
			return JSON.stringify(createFallbackDoc(this.accumulated));
		}
	}
}

class HtmlChunkStrategy implements ToolProcessorChunkStrategy {
	private accumulated = '';
	private schema: Schema;
	private useHtmlPlus: boolean;

	constructor(schema?: Schema) {
		this.schema = schema ?? defaultSchema;
		this.useHtmlPlus = expValEquals('platform_editor_use_html_plus_parser', 'isEnabled', true);
	}

	processChunk(delta: string): string {
		this.accumulated += delta;

		try {
			if (this.useHtmlPlus) {
				const adf = htmlToAdf(this.accumulated);
				if (!adf) {
					return JSON.stringify(createFallbackDoc(this.accumulated));
				}
				return JSON.stringify(adf as unknown as DocNode);
			}

			const element = getDocument()?.createElement('div');
			if (!element) {
				return JSON.stringify(createFallbackDoc(this.accumulated));
			}
			element.innerHTML = this.accumulated;
			return JSON.stringify(
				jsonTransformer.encode(DOMParser.fromSchema(this.schema).parse(element)) as DocNode,
			);
		} catch {
			return JSON.stringify(createFallbackDoc(this.accumulated));
		}
	}
}

export const createToolProcessorChunkStrategy = (
	contentType: MultiFormatContentType | undefined,
	schema?: Schema,
): ToolProcessorChunkStrategy => {
	switch (contentType) {
		case 'text/markdown':
			return new MarkdownChunkStrategy(schema);
		case 'text/html':
			return new HtmlChunkStrategy(schema);
		case 'application/adf+json':
		default:
			return new AdfChunkStrategy(schema);
	}
};
